/**
 * Where a deliberate request for background location should go, as a decision
 * with no dependencies.
 *
 * **This file imports nothing, and that is the point.** `stay-visible.ts` pulls
 * in expo-location, expo-task-manager and react-native, so nothing in it can be
 * imported by a test — the only assertions available there are scans of its own
 * source text. `presence-heartbeat.ts` already solved this the same way and says
 * so: `heartbeatVenuePatch` is "pure, and in `presence-heartbeat.ts` so it can
 * be driven by a real test rather than asserted against its own source."
 *
 * That distinction is not academic here. The first attempt at this fix shipped
 * with seven passing tests, all of them source scans. They proved the guard ran
 * before the request; not one of them could prove the guard was capable of
 * firing, and it was not.
 */

/** Where a deliberate request for background location should actually go. */
export type BackgroundRoute = "granted" | "ask" | "settings" | "unavailable";

/**
 * What the PREVIEW can report, which is the policy's outcomes plus "we could
 * not work it out".
 *
 * Deliberately a separate type rather than a fourth member of `BackgroundRoute`.
 * `requestBackgroundLocation` dispatches on a route and FALLS THROUGH to the
 * prompt for anything it does not match — so an "unknown" route would silently
 * become "ask" and fire a prompt. Keeping the policy union closed means that
 * value can never reach that dispatch at all, which is better than guarding it.
 */
export type BackgroundRoutePreviewResult = BackgroundRoute | "unknown";

/**
 * The decision, as a pure function, because the first attempt at this shipped
 * wrong and its tests could not have caught it.
 *
 * That version asked `getBackgroundPermissionsAsync` whether `canAskAgain` was
 * false and routed to Settings when it was. It never fired. The reason is in
 * expo-location's own native source
 * (`EXBackgroundLocationPermissionRequester.m:138`):
 *
 *     case kCLAuthorizationStatusAuthorizedWhenInUse: {
 *       if (_wasAsked) { status = EXPermissionStatusDenied; }
 *       else           { status = EXPermissionStatusUndetermined; }
 *
 * `_wasAsked` is an instance property initialised to `false` — **it resets on
 * every app launch.** So somebody with "While Using the App" reads back as
 * `undetermined` after a restart, and `EXPermissionsService.m:125` derives
 * `canAskAgain = status != denied`, which makes it **true**. The guard was
 * therefore false in exactly the state it existed for. It only ever worked on a
 * second tap inside one session, which is not the reported case.
 *
 * iOS genuinely does not expose the difference — `Allow Once` and `While Using`
 * are the same `authorizedWhenInUse` to Core Location — so the decision has to
 * be app-owned. Hence `attemptedBefore`.
 *
 * Pure and exported for the same reason `heartbeatVenuePatch` is: so it can be
 * driven by a real test rather than asserted against its own source. The
 * previous tests scanned this file's text, proved the guard ran before the
 * request, and could not possibly prove the guard could ever fire.
 */
export function backgroundPermissionRoute(input: {
  platform: "ios" | "android" | "web";
  backgroundStatus: string;
  canAskAgain: boolean;
  attemptedBefore: boolean;
  /** Foreground is `undetermined` — nothing has been asked in this install. */
  freshPermissionCycle: boolean;
}): BackgroundRoute {
  if (input.platform === "web") return "unavailable";
  if (input.backgroundStatus === "granted") return "granted";

  // Android's signal is real and reliable, so it decides there — first, and
  // alone. Neither the app-owned memory below nor the reinstall rule below it
  // is allowed a say: both exist to work around iOS behaviour that Android does
  // not have, and applying them here would change Android with no evidence for
  // it.
  //
  // This branch used to sit BELOW the fresh-cycle rule, which meant Android did
  // not in fact always route on `canAskAgain` however firmly the comment said
  // so. No Android state was found where the two disagree — reaching
  // `canAskAgain === false` requires having been prompted, which requires
  // foreground already granted, so foreground `undetermined` implies
  // `canAskAgain` is true and both paths say "ask". Moving it is therefore a
  // correction to the code's stated invariant rather than to its behaviour, and
  // is worth making for exactly that reason: an invariant that holds by luck
  // reads as one that holds by design.
  if (input.platform === "android") {
    return input.canAskAgain ? "ask" : "settings";
  }

  // From here down is iOS-only.
  //
  // A fresh cycle outranks our own memory of having asked, and it has to.
  // `storage` is the iOS keychain, and this repository already records that
  // "iOS keeps keychain items when an app is deleted, so even a reinstall
  // inherits the old one" (`storage.ts:92`). Location permissions do NOT
  // survive a reinstall. Without this line a reinstall would carry
  // `attemptedBefore` into a clean permission cycle and send somebody to
  // Settings without ever offering them the real prompt.
  //
  // Keyed on an OS-observable fact rather than a second bookkeeping system,
  // which also covers iOS's "Reset Location & Privacy" — a case
  // install-scoped storage would miss.
  if (input.freshPermissionCycle) return "ask";

  return input.attemptedBefore ? "settings" : "ask";
}

